import { useEffect, useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const base64UrlDecode = (str) => {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  try {
    return decodeURIComponent(
      binary
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch {
    return binary;
  }
};

const TIME_CLAIMS = new Set(["iat", "exp", "nbf", "auth_time"]);

// Registered JWT claims (RFC 7519) that benefit from a plain-English label
// next to the opaque three-letter key. iat/exp/nbf are already annotated with
// a formatted timestamp, so they are intentionally excluded here.
const REGISTERED_CLAIM_LABEL_KEYS = {
  iss: "jwt_claim_label_iss",
  sub: "jwt_claim_label_sub",
  aud: "jwt_claim_label_aud",
  jti: "jwt_claim_label_jti",
};

const formatRelativeDuration = (deltaSeconds, t) => {
  const abs = Math.abs(deltaSeconds);
  let value;
  let unitKey;
  if (abs < 60) {
    value = abs;
    unitKey = value === 1 ? "jwt_unit_second" : "jwt_unit_seconds";
  } else if (abs < 3600) {
    value = Math.floor(abs / 60);
    unitKey = value === 1 ? "jwt_unit_minute" : "jwt_unit_minutes";
  } else if (abs < 86400) {
    value = Math.floor(abs / 3600);
    unitKey = value === 1 ? "jwt_unit_hour" : "jwt_unit_hours";
  } else {
    value = Math.floor(abs / 86400);
    unitKey = value === 1 ? "jwt_unit_day" : "jwt_unit_days";
  }
  return t("jwt_duration_format", { value, unit: t(unitKey) });
};

const JWTDecoder = () => {
  const [input, setInput] = useState("");
  const { t } = useTranslation();
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 10_000);
    return () => clearInterval(id);
  }, []);

  const decoded = useMemo(() => {
    const token = input.trim();
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) {
      return { error: t("jwt_invalid_format") };
    }
    try {
      const header = JSON.parse(base64UrlDecode(parts[0]));
      const payload = JSON.parse(base64UrlDecode(parts[1]));
      return { header, payload, signature: parts[2] };
    } catch {
      return { error: t("jwt_invalid_format") };
    }
  }, [input, t]);

  const validityInfo = useMemo(() => {
    if (!decoded || decoded.error || !decoded.payload) return null;
    const { exp, iat, nbf } = decoded.payload;
    const hasExp = typeof exp === "number";
    const hasNbf = typeof nbf === "number";
    const hasIat = typeof iat === "number";
    if (!hasExp && !hasNbf && !hasIat) return null;
    const now = Math.floor(Date.now() / 1000);
    let state;
    let relative = null;
    if (hasNbf && nbf > now) {
      state = "not_yet_valid";
      relative = formatRelativeDuration(nbf - now, t);
    } else if (hasExp && exp < now) {
      state = "expired";
      relative = formatRelativeDuration(now - exp, t);
    } else if (hasExp) {
      state = "valid";
      relative = formatRelativeDuration(exp - now, t);
    } else {
      state = "info_only";
    }
    return {
      state,
      relative,
      expDate: hasExp ? new Date(exp * 1000).toLocaleString() : null,
      iatDate: hasIat ? new Date(iat * 1000).toLocaleString() : null,
      nbfDate: hasNbf ? new Date(nbf * 1000).toLocaleString() : null,
    };
  }, [decoded, t]);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const renderJsonWithTimes = (obj) => {
    const entries = Object.entries(obj);
    return (
      <pre className="bg-gray-900 text-green-200 p-4 rounded-md overflow-auto text-sm whitespace-pre-wrap break-all">
{`{
${entries
  .map(([k, v]) => {
    const value = JSON.stringify(v);
    let annotation = "";
    if (TIME_CLAIMS.has(k) && typeof v === "number") {
      annotation = `  // ${new Date(v * 1000).toLocaleString()}`;
    } else if (REGISTERED_CLAIM_LABEL_KEYS[k]) {
      annotation = `  // ${t(REGISTERED_CLAIM_LABEL_KEYS[k])}`;
    }
    return `  ${JSON.stringify(k)}: ${value}${annotation}`;
  })
  .join(",\n")}
}`}
      </pre>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">{t("jwt_decoder_title")}</h1>
            <p className="text-gray-600">{t("jwt_decoder_intro")}</p>

            <textarea
              rows={6}
              className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              placeholder={t("jwt_input_placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />

            <div className="flex gap-3">
              <StandardButton
                onClick={() => setInput("")}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("jwt_clear")}
              </StandardButton>
              <StandardButton
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setInput(text);
                  } catch {
                    toast.error(t("failed_to_copy"));
                  }
                }}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("jwt_paste")}
              </StandardButton>
            </div>

            {decoded?.error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
                {decoded.error}
              </div>
            )}

            {decoded && !decoded.error && (
              <>
                {validityInfo && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`p-3 rounded-md border ${
                      validityInfo.state === "expired"
                        ? "bg-red-50 border-red-200 text-red-700"
                        : validityInfo.state === "not_yet_valid"
                        ? "bg-amber-50 border-amber-200 text-amber-800"
                        : validityInfo.state === "valid"
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-gray-50 border-gray-200 text-gray-700"
                    }`}
                  >
                    {validityInfo.state === "not_yet_valid" && (
                      <div className="font-medium">
                        {t("jwt_not_yet_valid")} —{" "}
                        {t("jwt_becomes_valid_in", {
                          relative: validityInfo.relative,
                        })}
                      </div>
                    )}
                    {validityInfo.state === "expired" && (
                      <div className="font-medium">
                        {t("jwt_expired")} —{" "}
                        {t("jwt_expired_ago", {
                          relative: validityInfo.relative,
                        })}
                      </div>
                    )}
                    {validityInfo.state === "valid" && (
                      <div className="font-medium">
                        {t("jwt_expires_in", {
                          relative: validityInfo.relative,
                        })}
                      </div>
                    )}
                    {validityInfo.expDate && (
                      <div className="text-sm opacity-80 mt-1">
                        {t("jwt_valid_until")}: {validityInfo.expDate}
                      </div>
                    )}
                    {validityInfo.nbfDate && (
                      <div className="text-sm opacity-80 mt-1">
                        {t("jwt_not_before")}: {validityInfo.nbfDate}
                      </div>
                    )}
                    {validityInfo.iatDate && (
                      <div className="text-sm opacity-80 mt-1">
                        {t("jwt_issued_at")}: {validityInfo.iatDate}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-indigo-700">{t("jwt_header")}</strong>
                    <StandardButton
                      onClick={() =>
                        copyToClipboard(JSON.stringify(decoded.header, null, 2), t("jwt_header"))
                      }
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                  {renderJsonWithTimes(decoded.header)}
                </div>

                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-indigo-700">{t("jwt_payload")}</strong>
                    <StandardButton
                      onClick={() =>
                        copyToClipboard(JSON.stringify(decoded.payload, null, 2), t("jwt_payload"))
                      }
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                  {renderJsonWithTimes(decoded.payload)}
                </div>

                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-indigo-700">{t("jwt_signature")}</strong>
                    <StandardButton
                      onClick={() => copyToClipboard(decoded.signature, t("jwt_signature"))}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                  <p className="break-all font-mono text-sm text-gray-800">{decoded.signature}</p>
                  <p className="mt-2 text-xs text-gray-500">{t("jwt_signature_note")}</p>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default JWTDecoder;
